import { useEffect, useRef, type HTMLAttributes } from "react";
import { createAsciiReveal, type AsciiRevealController } from "ascii-reveal";
import { usePageVisible } from "./use-page-visible";

// A short entrance, never tied to query refreshes or live content updates.
export function EntryReveal({
  as,
  paused,
  onView = false,
  lang,
  className = "",
  children,
  ...props
}: HTMLAttributes<HTMLElement> & {
  as: "h1" | "section";
  paused: boolean;
  onView?: boolean;
}) {
  const Tag = as;
  const element = useRef<HTMLElement>(null);
  const played = useRef(false);
  const visible = usePageVisible();

  useEffect(() => {
    const root = element.current;
    if (!root || played.current || paused || !visible) return;

    let stopped = false;
    let reveal: AsciiRevealController | undefined;
    const play = () => {
      if (stopped || played.current) return;
      played.current = true;
      // Measure the final typeface, especially the Arabic headings.
      void document.fonts.ready.then(() => {
        if (stopped) return;
        reveal = createAsciiReveal(root, {
          preset: "decode",
          duration: "fast",
          speed: 1.4,
          lazy: false,
        });
        void reveal.reveal();
      });
    };
    const observer = onView
      ? new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer?.disconnect();
            play();
          },
          { rootMargin: "0px 0px -32px 0px" },
        )
      : null;
    if (observer) observer.observe(root);
    else play();
    return () => {
      stopped = true;
      observer?.disconnect();
      reveal?.destroy();
    };
  }, [paused, visible, lang, onView]);

  return (
    <Tag
      {...props}
      lang={lang}
      ref={(node) => {
        element.current = node;
      }}
      className={`entry-reveal ${className}`}
    >
      {children}
    </Tag>
  );
}
